/**
 * @description Component-ScaleLimitPreview
 */
import Modal from '@/components/Modal';
import './index.less';

import Alert from '@/components/Alert';
import { Button } from '@/components/Button';
import { GlobalModalType } from '@/constants';
import { useGlobalConfig, useModalOpen, useToggleModal } from '@/features/global/hooks';
import { useCurrentPairByDevice } from '@/features/pair/hook';
import { batchPlaceScaledLimitOrder } from '@/features/trade/actions';
import { useScaleSimulation, useTradeSide, useTradeStoreTransformed } from '@/features/trade/hooks';
import { useSimulateBatchPlaceScaledLimitOrder } from '@/features/trade/query';
import { useSDK } from '@/features/web3/hook';
import { useAppDispatch } from '@/hooks';
import { useTxNotification } from '@/hooks/useTxNotification';
import { useChainId, useProvider, useUserAddr } from '@/hooks/web3/useChain';
import { useWalletSigner } from '@/hooks/web3/useWalletNetwork';
import { toWad } from '@/utils/numberUtil';
import { Side } from '@synfutures/sdks-perp';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LimitFormDetail } from '../../TradeLimitForm/LimitFormDetail';
import ScaleLimitPreviewContent from './ScaleLimitPreviewContent';
interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
}
const ScaleLimitPreview: FC<IPropTypes> = function ({}) {
  const modalOpen = useModalOpen(GlobalModalType.ORDER_PREVIEW);
  const toggleModal = useToggleModal(GlobalModalType.ORDER_PREVIEW);
  const { t } = useTranslation();
  const signer = useWalletSigner();
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const tradeSide = useTradeSide(chainId);
  const globalConfig = useGlobalConfig(chainId);
  const sdkContext = useSDK(chainId);
  const scaleFormState = useTradeStoreTransformed();
  const currentPair = useCurrentPairByDevice(chainId);
  const simulation = useScaleSimulation(chainId);
  const provider = useProvider();
  const dispatch = useAppDispatch();
  const { data: scaleSimulation, isLoading } = useSimulateBatchPlaceScaledLimitOrder(chainId);
  const notification = useTxNotification();
  const tradeText = useMemo(() => {
    return tradeSide === Side.LONG ? t('common.buyUpper') : t('common.sellUpper');
  }, [tradeSide, t]);
  return (
    <Modal
      width={640}
      centered={true}
      title={
        <div className="syn-scale-limit-preview-title">
          <div className="syn-scale-limit-preview-title-left">
            <span>{t('common.tradePage.orderView')}</span>
            {t('common.tradePage.limitOrders', { number: simulation?.data?.orders.length })}
          </div>
        </div>
      }
      className="syn-scale-limit-preview"
      open={modalOpen}
      footer={
        <div className="syn-scale-limit-preview-footer">
          <LimitFormDetail
            showSize={true}
            baseToken={currentPair?.rootInstrument.baseToken}
            isLoading={false}
            marginToken={currentPair?.rootInstrument.marginToken}
            chainId={chainId}
            simulationData={scaleSimulation?.data}
          />
          <div className="syn-scale-limit-preview-footer-right">
            <Alert showIcon={true} type="warning" message={t('common.tradePage.warningMsg.scaleLimitNotice')} />
            <Button
              type="primary"
              loading={isLoading}
              onClick={async () => {
                const baseAmountWad = toWad(scaleFormState?.tradeSize || 0);
                if (
                  signer &&
                  chainId &&
                  userAddr &&
                  sdkContext &&
                  currentPair &&
                  provider &&
                  simulation?.data &&
                  !baseAmountWad.eq(0) &&
                  globalConfig.deadline &&
                  scaleFormState.leverage
                ) {
                  try {
                    const result = await dispatch(
                      batchPlaceScaledLimitOrder({
                        signer,
                        chainId,
                        userAddr,
                        sdkContext,
                        pair: currentPair,
                        provider,
                        simulation: simulation.data,
                        deadline: Number(globalConfig.deadline),
                        side: tradeSide,
                        leverageWad: toWad(scaleFormState.leverage),
                        baseSizeWad: baseAmountWad,
                        sendTxNotification: notification?.open,
                      }),
                    ).unwrap();
                    if (result) {
                      toggleModal();
                    }
                  } catch (e) {
                    console.log('🚀 ~ e:', e);
                  }
                }
              }}
              className={tradeSide === Side.SHORT ? 'short' : 'long'}
              chainIconProps={chainId && { chainId, width: 18, marginRight: 4 }}>
              {tradeText}
            </Button>
          </div>
        </div>
      }
      onCancel={() => toggleModal()}>
      <ScaleLimitPreviewContent />
    </Modal>
  );
};

export default ScaleLimitPreview;
